import {t} from "@lingui/core/macro";
import {useId, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {
    ModalDialog,
    ModalFooter,
    ModalLauncher,
    ModalPanel,
} from "@khanacademy/wonder-blocks-modal";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import Switch from "@khanacademy/wonder-blocks-switch";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {
    Body,
    HeadingMedium,
    LabelLarge,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@repo/analytics-learning-time";
import {useMutation, useQuery} from "@repo/data-gql";
import {useSetCollectInterestsMutation} from "@repo/guide-core";
import {GuideColors, KhanmigoFrontLogo, XCircleIcon} from "@repo/guide-ui";
import {a11yStyles} from "@repo/shared-styles";

import {renderErrorToast} from "#notifications-package/toast-notification.tsx";

import {DELETE_ALL_INTERESTS_MUTATION} from "./graphql/delete-all-interests-mutation.ts";
import {DELETE_INTEREST_MUTATION} from "./graphql/delete-interest-mutation.ts";
import {GUIDE_INTERESTS_QUERY} from "./graphql/guide-interests-query.ts";

const IntroMessage = ({text}: {text: string}) => (
    <View style={styles.message}>
        {/*TODO(carter) Update svg settings so that khanmigo logo is consistent no matter what message is displayed*/}
        <KhanmigoFrontLogo />
        <View style={styles.messageBubbleWrapper}>
            <Body style={[styles.messageBubble, styles.botMessage]}>
                {text}
            </Body>
        </View>
    </View>
);

type InterestMessageProps = {
    text: string;
    onDelete: () => Promise<void>;
};

const InterestMessage = ({text, onDelete}: InterestMessageProps) => {
    const [isDeleting, setIsDeleting] = useState(false);

    const handleClick = async () => {
        setIsDeleting(true);
        await onDelete();
        setIsDeleting(false);
    };

    return (
        <View style={styles.message}>
            <View
                style={[
                    styles.messageBubbleWrapper,
                    {justifyContent: "flex-end"},
                ]}
            >
                <Body style={[styles.messageBubble, styles.userMessage]}>
                    {text}
                </Body>
                {isDeleting ? (
                    <CircularSpinner size="small" />
                ) : (
                    <Clickable onClick={handleClick}>
                        {() => <XCircleIcon />}
                    </Clickable>
                )}
            </View>
        </View>
    );
};

type InterestsToggleDialogProps = {
    isTurningOff: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    isLoading: boolean;
};

const InterestsToggleDialog = ({
    isTurningOff,
    onCancel,
    onConfirm,
    isLoading,
}: InterestsToggleDialogProps) => {
    const dialogText = isTurningOff
        ? t`Are you sure you want to turn off Khanmigo Interests? By turning this off, all of your interests will be deleted.`
        : t`You are about to turn on Khanmigo Interests. By turning this on, Khanmigo will be able to learn from your conversations and personalize your learning process.`;
    const buttonColor = isTurningOff ? "destructive" : "progressive";
    const buttonText = isTurningOff
        ? t`Turn off and delete Interests`
        : t`Turn on Interests`;
    // The ID used by Screen Readers to identify the dialog title.
    const dialogTitleId = useId();
    const dialogDescId = useId();

    return (
        <ModalDialog
            style={styles.dialog}
            aria-labelledby={dialogTitleId}
            aria-describedby={dialogDescId}
            role="alertdialog"
        >
            <ModalPanel
                closeButtonVisible={false}
                content={
                    <View>
                        <HeadingMedium
                            id={dialogTitleId}
                            style={a11yStyles.srOnly}
                        >
                            {t`Are you sure?`}
                        </HeadingMedium>
                        <p id={dialogDescId}>{dialogText}</p>
                    </View>
                }
                footer={
                    <ModalFooter>
                        <View style={styles.dialogFooter}>
                            <Button
                                onClick={onCancel}
                                kind="secondary"
                                disabled={isLoading}
                            >
                                {t`Cancel`}
                            </Button>
                            <Button
                                onClick={onConfirm}
                                actionType={buttonColor}
                                spinner={isLoading}
                            >
                                {buttonText}
                            </Button>
                        </View>
                    </ModalFooter>
                }
            />
        </ModalDialog>
    );
};

type GuideInterestsContentProps = {
    kaid: string;
};

const GuideInterestsContent = ({kaid}: GuideInterestsContentProps) => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const setCollectInterests = useSetCollectInterestsMutation();
    const [showInterestsToggleDialog, setShowInterestsToggleDialog] =
        useState(false);
    const [dialogIsLoading, setDialogIsLoading] = useState(false);

    const [interestsResult, refetchInterests] = useQuery(
        GUIDE_INTERESTS_QUERY,
        {
            variables: {kaid},
            fetchPolicy: FetchPolicy.NetworkOnly,
        },
    );
    const [deleteInterest] = useMutation(DELETE_INTEREST_MUTATION);
    const [deleteAllInterests] = useMutation(DELETE_ALL_INTERESTS_MUTATION);

    if (
        interestsResult.status === "loading" ||
        interestsResult.status === "no-data"
    ) {
        return <CircularSpinner size="small" style={{padding: 12}} />;
    }

    if (interestsResult.status !== "success") {
        return (
            <Body>
                {t`An error occurred while loading your interests. Please try again later.`}
            </Body>
        );
    }

    const {user} = interestsResult.data;
    const interests = user?.aiGuideUserInterests ?? [];
    const trackingIsOn = user?.isCollectingInterests ?? false;

    const handleToggleModalConfirmation = async () => {
        setDialogIsLoading(true);
        if (trackingIsOn) {
            await deleteAllInterests({kaid});

            publishAnalyticsEvent({
                eventType: "AiGuideSettingsInterestTrackingDisabled",
                eventSchemaVersion: 1,
            });
        } else {
            publishAnalyticsEvent({
                eventType: "AiGuideSettingsInterestTrackingEnabled",
                eventSchemaVersion: 1,
            });
        }
        await setCollectInterests(kaid, !trackingIsOn);
        refetchInterests();
        setShowInterestsToggleDialog(false);
        setDialogIsLoading(false);
    };

    const handleDeleteInterest = async (kaid: string, key: string) => {
        const renderError = () => {
            renderErrorToast(t`Unable to delete interest.`);
        };

        try {
            const result = await deleteInterest({kaid, key});
            // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ deleteInterest: deleteGuideInterest_deleteInterest | null | undefined; }>'.
            if (result.data?.deleteInterest?.error === null) {
                publishAnalyticsEvent({
                    eventType: "AiGuideSettingsInterestDeletionSubmitted",
                    eventSchemaVersion: 1,
                });
                refetchInterests();
            } else {
                renderError();
            }
        } catch (e: any) {
            renderError();
        } finally {
            Promise.resolve();
        }
    };

    const khanmigoMessage = interests?.length
        ? t`Hello, here are the top 10 interesting things I noticed about our past conversations. Feel free to remove any that don't seem accurate!`
        : t`I haven't identified any of your interests yet. Start chatting with me so I can learn about you!`;

    const interestsLabel = trackingIsOn
        ? t`Interests turned ON`
        : t`Interests turned OFF`;
    return (
        <>
            <View style={styles.trackingToggle}>
                <Tooltip
                    content={t`Toggle to turn on/off interests`}
                    forceAnchorFocusivity={false}
                    placement="bottom"
                >
                    <Switch
                        aria-label={t`Interests`}
                        checked={trackingIsOn}
                        id="interests-switch"
                        onChange={() => setShowInterestsToggleDialog(true)}
                    />
                </Tooltip>
                <LabelSmall
                    // We use the aria-hidden attribute here because the label is defined on the switch input itself
                    aria-hidden="true"
                    htmlFor="interests-switch"
                    style={styles.switchLabel}
                    tag="label"
                >
                    {interestsLabel}
                </LabelSmall>
            </View>
            <Strut size={spacing.small_12} />
            {trackingIsOn && (
                <>
                    <IntroMessage text={khanmigoMessage} />
                    {interests &&
                        interests.map(({combinedKey, interest}) => (
                            <InterestMessage
                                key={combinedKey}
                                onDelete={async () => {
                                    await handleDeleteInterest(
                                        kaid,
                                        combinedKey ?? "",
                                    );
                                }}
                                text={interest ?? ""}
                            />
                        ))}
                </>
            )}
            <ModalLauncher
                opened={showInterestsToggleDialog}
                onClose={() => {}}
                modal={() => (
                    <InterestsToggleDialog
                        isTurningOff={trackingIsOn}
                        onCancel={() => setShowInterestsToggleDialog(false)}
                        onConfirm={() => {
                            handleToggleModalConfirmation();
                        }}
                        isLoading={dialogIsLoading}
                    />
                )}
            />
        </>
    );
};

type GuideInterestsProps = {
    kaid: string;
};

const GuideInterests = ({kaid}: GuideInterestsProps) => {
    return (
        <View>
            <LabelLarge>{t`Personalize your experience!`}</LabelLarge>
            <LabelSmall>
                {t`Khanmigo can make your experience even more personal and the learning process more effective by learning from your past conversations.`}
                <br />
                <br />
                {t`We keep the top 10 most interesting things from your conversations with Khanmigo and it will keep getting better the more you interact with it. You can turn it off at any time. You can also delete any Interest you don’t like.`}
            </LabelSmall>
            <Strut size={spacing.small_12} />
            <GuideInterestsContent kaid={kaid} />
        </View>
    );
};

export default GuideInterests;

const styles = {
    dialog: {
        maxHeight: 200,
        maxWidth: 500,
    },
    dialogFooter: {
        flexDirection: "row",
        gap: spacing.medium_16,
    },
    message: {
        flexDirection: "row",
        alignItems: "top",
        width: "100%",
        marginTop: spacing.xxxSmall_4,
        marginBottom: spacing.xxxSmall_4,
    },
    trackingToggle: {
        flexDirection: "row",
        alignItems: "center",
    },
    messageBubbleWrapper: {
        flexDirection: "row",
        width: "100%",
    },
    messageBubble: {
        flexDirection: "row",
        borderRadius: 12,
        padding: "8px 16px",
        maxWidth: "90%",
        width: "fit-content",
    },
    userMessage: {
        backgroundColor: GuideColors.darkPurple,
        color: color.white,
        marginRight: spacing.small_12,
        alignSelf: "flex-end",
        borderBottomRightRadius: 0,
    },
    botMessage: {
        alignSelf: "flex-start",
        backgroundColor: GuideColors.peach,
        borderTopLeftRadius: 0,
    },
    switchLabel: {
        marginLeft: spacing.xSmall_8,
    },
} as const;
